import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { functionErrorMessage } from '@/lib/functions';
import type { EmployeeSales } from './columns';

export type EmployeeListItem = {
  id: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string | null;
  is_active: boolean;
  invited_at: string | null;
  auth_user_id: string | null;
  primary_branch_id: string | null;
  job_titles: { id: string; name: string } | null;
  roles: { id: string; name: string } | null;
  branches: { id: string; name: string } | null;
  comax_employee_code: string | null;
  job_title_id: string | null;
  role_id: string | null;
  birth_date: string | null;
  /** נבנית מהחלקים בשרת (מיגרציה 094). לקריאה בלבד. */
  address: string | null;
  city: string | null;
  street: string | null;
  house_number: string | null;
  apartment: string | null;
  start_date: string | null;
  /** יום העבודה האחרון, כשנקבע מראש. null = אין סיום מתוכנן. */
  employment_end_date: string | null;
  /** מכסת שעות. null = בלי מכסה, ולא אפס. */
  weekly_hours_target: number | null;
  monthly_hours_target: number | null;
  /** סניפים נוספים מעבר לראשי (PRD 5.2). ריק אצל רוב העובדים. */
  employee_branches: { branch_id: string; branches: { id: string; name: string } | null }[];
};

export type EmployeeInput = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string | null;
  birth_date: string | null;
  city: string | null;
  street: string | null;
  house_number: string | null;
  apartment: string | null;
  start_date: string | null;
  job_title_id: string | null;
  role_id: string;
  primary_branch_id: string | null;
  comax_employee_code: string | null;
  weekly_hours_target: number | null;
  monthly_hours_target: number | null;
};

const LIST_KEY = ['employees'] as const;
const OPTIONS_KEY = ['employee-options'] as const;

/**
 * המזהים עצמם ולא רק השמות המקושרים: בלעדיהם טופס העריכה נפתח בלי
 * תפקיד ובלי הרשאות, והשמירה מרוקנת אותם לעובד. אותו דבר לתאריך
 * הלידה, הכתובת ותאריך תחילת העבודה.
 *
 * בלי הערות בתוך המחרוזת: PostgREST מקבל אותה כרשימת עמודות, ו --
 * הוא תו חוקי בשם עמודה מבחינתו, לא תחילת הערה.
 */
const SELECT = `
  id, first_name, last_name, phone, email, is_active, invited_at, auth_user_id,
  primary_branch_id, comax_employee_code,
  job_title_id, role_id, birth_date, address, start_date, employment_end_date,
  city, street, house_number, apartment,
  weekly_hours_target, monthly_hours_target,
  job_titles ( id, name ),
  roles ( id, name ),
  branches!employees_primary_branch_id_fkey ( id, name ),
  employee_branches ( branch_id, branches ( id, name ) )
`;

export function useEmployees() {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async (): Promise<EmployeeListItem[]> => {
      const { data, error } = await supabase
        .from('employees')
        .select(SELECT)
        // חשבון התמיכה של הספק אינו עובד של הרשת (מיגרציה 014)
        .eq('is_support', false)
        .order('is_active', { ascending: false })
        .order('first_name');
      if (error) throw error;
      return (data ?? []) as unknown as EmployeeListItem[];
    },
  });
}

/** הרשימות שהטופס בוחר מהן. כולן מהטבלאות, אף אחת לא קשיחה בקוד. */
export function useEmployeeOptions() {
  return useQuery({
    queryKey: OPTIONS_KEY,
    queryFn: async () => {
      const [branches, jobTitles, roles] = await Promise.all([
        supabase
          .from('branches')
          .select('id, name, color')
          .eq('is_active', true)
          .order('sort_order'),
        supabase
          .from('job_titles')
          .select('id, name')
          .eq('is_active', true)
          .order('sort_order'),
        supabase.from('roles').select('id, name, scope').order('name'),
      ]);
      if (branches.error) throw branches.error;
      if (jobTitles.error) throw jobTitles.error;
      if (roles.error) throw roles.error;

      return {
        branches: branches.data ?? [],
        jobTitles: jobTitles.data ?? [],
        roles: roles.data ?? [],
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * תפקידי ההרשאות שמותרים לתפקיד שנבחר (מיגרציה 091).
 *
 * בשרת ולא סינון בדפדפן: התקרה נאכפת ממילא בטריגר, והמסך רק
 * צריך להציג את אותה אמת. שני מימושים של אותו כלל היו מתפצלים.
 */
export function useRolesForJobTitle(jobTitleId: string | null) {
  return useQuery({
    queryKey: ['roles-for-job-title', jobTitleId],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ id: string; name: string; scope: string }[]> => {
      const { data, error } = await supabase.rpc('roles_for_job_title', {
        p_job_title: jobTitleId ?? undefined,
      });
      if (error) throw error;
      return (data ?? []) as { id: string; name: string; scope: string }[];
    },
  });
}

export function useEmployeeMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: LIST_KEY });

  const create = useMutation({
    mutationFn: async (values: EmployeeInput): Promise<string> => {
      const { data, error } = await supabase
        .from('employees')
        .insert(values)
        .select('id')
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: Partial<EmployeeInput> }) => {
      const { error } = await supabase.from('employees').update(values).eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  /**
   * השבתה ולא מחיקה: ההיסטוריה נשמרת (PRD 2.3), והכניסה נחסמת מיד
   * כי כל מדיניות RLS עוברת דרך current_employee_id שבודקת is_active.
   */
  const setActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('employees')
        .update({ is_active: isActive })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  /**
   * סניפים נוספים. פעולה נפרדת מהעובד עצמו, כי היא כותבת לטבלת
   * קשר ולא לעמודה, ובפקודה אחת כדי שלא יהיה רגע בלי שיוך.
   */
  const setBranches = useMutation({
    mutationFn: async (input: { employeeId: string; branchIds: string[] }) => {
      const { error } = await supabase.rpc('set_employee_branches', {
        p_employee: input.employeeId,
        p_branches: input.branchIds,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  /**
   * קביעה, הקדמה או ביטול של סיום העסקה. פעולה אחת בשרת, כדי
   * שהתאריך ודגל הפעילות לא ייפרדו: תאריך שעבר מנתק מיד, תאריך
   * עתידי משאיר פעיל, ו null מבטל ומחזיר לפעילות.
   */
  const setEmploymentEnd = useMutation({
    mutationFn: async (input: { employeeId: string; date: string | null }) => {
      const { error } = await supabase.rpc('set_employment_end', {
        p_employee: input.employeeId,
        p_date: input.date as string,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  /**
   * מחיקת כרטיס שנפתח בטעות (מיגרציה 068).
   *
   * השרת הוא שמחליט אם מותר: מי שכבר נכנס או שיש לו רישומים
   * אינו נמחק, והשגיאה שחוזרת אומרת למה. כאן לא בודקים שוב, כדי
   * שלא יהיו שני מקורות אמת שנפרדים זה מזה.
   */
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_employee', { p_id: id });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  /**
   * הודעת קליטה לעובד חדש (מיגרציה 074).
   *
   * השרת הוא שמחליט אם לשלוח: invited_at מונע כפילות, ו force
   * הוא בקשה מפורשת לשלוח שוב למי שלא קיבל.
   */
  const sendWelcome = useMutation({
    mutationFn: async (input: { employeeId: string; force?: boolean }) => {
      const { data, error } = await supabase.functions.invoke('send-welcome', {
        body: { employee_id: input.employeeId, force: input.force === true },
      });
      if (error) throw new Error(await functionErrorMessage(error));
      return data as { ok: boolean; data?: { sent: boolean; already?: boolean } };
    },
    onSuccess: invalidate,
  });

  return { create, update, setActive, setBranches, setEmploymentEnd, remove, sendWelcome };
}

/**
 * מכירות ויעד לכל עובד בחודש הנוכחי, למפתח לפי מזהה עובד.
 * נטען רק כשעמודות המכירות מוצגות, כדי לא לשלם עליו סתם.
 */
export function useEmployeeSales(enabled: boolean) {
  return useQuery({
    queryKey: ['employee-sales'],
    enabled,
    queryFn: async (): Promise<Record<string, EmployeeSales>> => {
      const { data, error } = await supabase.rpc('employees_sales_summary');
      if (error) throw error;
      return (data ?? {}) as unknown as Record<string, EmployeeSales>;
    },
  });
}
